import classNames from 'classnames'
import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import CreateSessionModal from '../modals/CreateSession'
import DeleteSessionModal from '../modals/DeleteSession'
import UpdateSessionModal from '../modals/UpdateSession'
import DeleteOrganisationModal from './modal/DeleteOrganisation'

import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import FolderZipIcon from '@mui/icons-material/FolderZip'
import { Box, Button, Grid, IconButton, List, ListItem, Typography } from '@mui/material'
import { saveAs } from 'file-saver'
import JSZip from 'jszip'
// import { getHeaders, getUser } from '../../../utils/common'
import ErrorDisplay from '../../components/error/ErrorDisplay'
import { DeployServersList } from './OrgDeployServer'
import { UserList } from './OrgUsersList'
import { OrgInfo, SessionInfo } from '../StudioHome'
import { axiosPrivate } from '../../utils/axiosInstance'
import StatusBar from '../../components/StatusBar'
import Loading from '../../Loading'
// import { useTypedSelector } from '../../../redux/useTypeSelector'
import '../../benthosPages/global.css'
export type appParams = {
  organisationID: string
}

/**
 * Organisation Page
 * @returns {JSX.Element}
 */
export default function Organisation() {
  // const { getUser } = useTypedSelector((state) => state.state)
  const navigate = useNavigate()

  const { organisationID } = useParams<appParams>()

  const [isLoading, setLoading] = useState(true)
  const [organisation, setOrganisation] = useState<OrgInfo | null>(null)
  const [loadError, setLoadError] = useState(null)

  // Modals
  const [deleteSessionModalIsOpen, setDeleteSessionModalIsOpen] = useState(false)
  const [deleteSessionModalTarget, setDeleteSessionModalTarget] = useState<SessionInfo | null>(null)
  const [updateSessionModalIsOpen, setUpdateSessionModalIsOpen] = useState(false)

  const [deleteOrgModalIsOpen, setDeleteOrgModalIsOpen] = useState(false)
  const [createSessionModalIsOpen, setCreateSessionModalIsOpen] = useState(false)

  // Sessions
  const [isLoadingSessions, setLoadingSessions] = useState(true)
  const [sessions, setSessions] = useState<SessionInfo[]>([])
  const [sessionSearch, setSessionSearch] = useState('')
  const myHeaders = new Headers()

  const getSessions = useCallback(() => {
    let subscribed = true

    fetch(`/mqttapi/v1/organisation/${organisationID}/sessions`, {
      headers: myHeaders,
      method: 'GET',
    })
      .then((response) => response.json())
      .then((json) => (subscribed ? setSessions(json) : null))
      .catch((error) => console.error(error))
      .finally(() => (subscribed ? setLoadingSessions(false) : null))

    return () => (subscribed = false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationID])

  const refreshSessions = useCallback(() => {
    setSessions([])
    setLoadingSessions(true)
    getSessions()
  }, [setSessions, getSessions, setLoadingSessions])

  useEffect(() => {
    let subscribed = true

    setLoading(false)
    setLoadError(null)
    setOrganisation(null)

    fetch(`/mqttapi/v1/organisation/${organisationID}`, {
      headers: myHeaders,
      method: 'GET',
    })
      .then((response) => response.json())
      .then((json) => {
        if (!subscribed) {
          return
        }
        setOrganisation(json)
        refreshSessions()
      })
      .catch((error) => setLoadError(error))
      .finally(() => (subscribed ? setLoading(false) : null))

    return () => {
      subscribed = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationID, refreshSessions])

  let sessionAddBtn = null
  if ((sessions || []).length > 0) {
    sessionAddBtn = (
      <IconButton
        title="create a session"
        onClick={() => {
          setCreateSessionModalIsOpen(true)
        }}
      >
        <AddBoxOutlinedIcon />
      </IconButton>
    )
  }
  /**
   * save zip file for complete session
   * @param zipName name of zip file
   * @param urls all urls of files
   * @returns nil
   */
  const saveZip = (zipName: string, sessionId: string) => {
    setLoadingSessions(true)
    axiosPrivate({ url: `/mqttapi/v1/session/${sessionId}/files`, method: 'get' })
      .then((resp) => {
        const files = resp.data.files.map((value: any) => value.name)
        const urls = files.map((fileName: string) => {
          return `/mqttapi/v1/session/${sessionId}/download/${fileName}`
        })
        if (!urls) return

        const zip = new JSZip()
        const folder = zip.folder(zipName) // folder name where all files will be placed in

        urls.forEach((url: string) => {
          const blobPromise = fetch(url, { headers: myHeaders, method: 'GET' }).then((r) => {
            if (r.status === 200) return r.blob()
            return Promise.reject(new Error(r.statusText))
          })
          const name = url.substring(url.lastIndexOf('/') + 1)
          folder?.file(name, blobPromise)
        })

        zip.generateAsync({ type: 'blob' }).then((blob) => saveAs(blob, zipName))
      })
      .catch((error) => {
        return
      })
      .finally(() => {
        setLoadingSessions(false)
      })
  }

  if (loadError !== null) {
    return <ErrorDisplay err={loadError} />
  }

  return (
    <div>
      <DeleteOrganisationModal
        organisation={organisation}
        isOpen={deleteOrgModalIsOpen}
        closeModal={() => setDeleteOrgModalIsOpen(false)}
        onDelete={() => {
          navigate('/app/view/user')
        }}
      />
      <DeleteSessionModal
        isOpen={deleteSessionModalIsOpen}
        session={deleteSessionModalTarget}
        closeModal={() => setDeleteSessionModalIsOpen(false)}
        onDelete={refreshSessions}
      />
      {(sessions || []).length > 0 && deleteSessionModalTarget && (
        <UpdateSessionModal
          isOpen={updateSessionModalIsOpen}
          session={deleteSessionModalTarget}
          closeModal={() => setUpdateSessionModalIsOpen(false)}
          onDelete={refreshSessions}
        />
      )}
      <CreateSessionModal
        forOrg={organisationID}
        orgName={organisation?.name}
        isOpen={createSessionModalIsOpen}
        closeModal={() => setCreateSessionModalIsOpen(false)}
        onCreate={(sessionID: string) => {
          navigate(`/app/view/s/${sessionID}`)
        }}
      />
      <StatusBar
        crumbs={{
          organisation: {
            id: organisationID || '',
            name: organisation?.name || '',
          },
        }}
      ></StatusBar>
      <Grid container spacing={3} sx={{ marginBottom: '5px' }}>
        <Grid item xs={12} md={6}>
          <div>
            {
              /* SESSIONS */ organisation && (
                <div>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>Sessions</Box>
                    <Box>{sessionAddBtn}</Box>
                  </Box>
                  {(sessions || []).length > 1 ? (
                    <input
                      className={classNames('form-control', "search")}
                      type="text"
                      value={sessionSearch}
                      onChange={(event) => setSessionSearch(event.currentTarget.value)}
                      placeholder="🔍 Search..."
                    />
                  ) : null}
                  {isLoadingSessions ? (
                    <Loading/>
                  ) : (
                    <List>
                      {sessions
                        .filter((opt) => {
                          if (sessionSearch === '') {
                            return true
                          }
                          const searchTerms = sessionSearch.split(' ')
                          const content = opt.name.toLowerCase()
                          return searchTerms.every((term) => {
                            return content.includes(term.toLowerCase())
                          })
                        })
                        .map((session, idx) => (
                          <ListItem
                            key={idx}
                            secondaryAction={
                              <Box sx={{ display: 'flex', justifyContent: 'right', textDecoration: 'none', alignItems: 'center' }}>
                                <Typography variant="body1">files: {session.files ? session.files : 0}</Typography>
                                <Typography variant="body1" sx={{ marginLeft: '2px' }}>
                                  Deploy: {session.deploy ? session.deploy : 0}
                                </Typography>
                                <IconButton
                                  title="download session as Zip"
                                  onClick={() => saveZip(`session-${session?.name}`, session.id)}
                                  color="success"
                                >
                                  <FolderZipIcon />
                                </IconButton>
                                <IconButton
                                  onClick={() => {
                                    setDeleteSessionModalTarget(session)
                                    setUpdateSessionModalIsOpen(true)
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  onClick={() => {
                                    setDeleteSessionModalIsOpen(true)
                                    setDeleteSessionModalTarget(session)
                                  }}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            }
                            sx={{ marginBottom: '10px', borderRadius: '5px' }}
                          >
                            <Link style={{ textDecoration: 'none' }} to={`/app/view/s/${session.id}`}>
                              {session.name}
                            </Link>
                          </ListItem>
                        ))}
                    </List>
                  )}
                  {!isLoadingSessions && (sessions || []).length === 0 ? (
                    <>
                      <button
                        title="create a session"
                        className={"firstSessionBtn"}
                        onClick={() => {
                          setCreateSessionModalIsOpen(true)
                        }}
                      >
                        Create your first session
                      </button>
                      <div className={"firstSessionBlurb"}>
                        <div className={"firstSessionHand"}>☝️</div>
                        Click here to create your first session. Sessions are a place where Carbon configs can be created, linted and tested
                        against a specific version of Carbon.
                      </div>
                    </>
                  ) : null}
                </div>
              )
            }
            {organisation && <UserList userids={organisation.userids} />}
            {organisation && <DeployServersList />}
          </div>
        </Grid>
        <Grid item xs={12} md={6}>
          <div>
            {isLoading ? (
              <Loading action="Loading organisation info" />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <h4>Organisation {organisation?.name}</h4>
                <body>ID: {organisation?.id}</body>
                {/* {getUser?.user_type === 'Primary Owner' && organisation && ( */}
                  <Button
                    onClick={() => {
                      setDeleteOrgModalIsOpen(true)
                    }}
                    color="error"
                    variant="contained"
                  >
                    Delete
                  </Button>
                {/* )} */}
              </div>
            )}
          </div>
        </Grid>
      </Grid>
    </div>
  )
}
